import React, { useState, useRef } from 'react';
import { useTranslation } from 'context/LanguageContext';
import { Menu, X } from 'lucide-react';

const Categories = ({ categories = [], onCategorySelect }) => {
  const { language, t } = useTranslation();
  const isRTL = language === 'ar';
  
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const buttonRefs = useRef({});

  const fallbackCategories = [
    { id: 1, name: 'Electronics' },
    { id: 2, name: 'Fashion' },
    { id: 3, name: 'Home & Garden' },
    { id: 4, name: 'Sports' },
    { id: 5, name: 'Books' },
  ];

  const displayCategories = categories.length > 0 ? categories : fallbackCategories;

  const handleCategoryClick = (category) => {
    const isSameCategory = selectedCategory === category.id;
    const nextCategoryId = isSameCategory ? null : category.id;

    setSelectedCategory(nextCategoryId);
    onCategorySelect?.(nextCategoryId);
  };

  const handleDialogCategoryClick = (category) => {
    handleCategoryClick(category);
    setIsCategoryDialogOpen(false);
  };

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'}>
      <nav className="relative bg-white">
        <div className="mx-auto px-4">
          <div className="flex items-center gap-2">

            <button
              type="button"
              onClick={() => setIsCategoryDialogOpen(true)}
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 hover:border-gray-400 transition-all flex-shrink-0"
              aria-label="Show all categories"
            >
              <Menu size={18} />
            </button>

            <div className="flex-1 overflow-x-auto overflow-y-visible scrollbar-hide">
              <ul className="flex items-center gap-2 whitespace-nowrap py-2">
                {displayCategories.map((category) => {
                  const isSelected = selectedCategory === category.id;
                  return (
                    <li key={category.id} className="relative">
                      <button
                        ref={(el) => buttonRefs.current[category.id] = el}
                        className={`
                          flex items-center justify-center min-w-[110px] py-2 px-3
                          border rounded-lg transition-all text-sm font-medium
                          ${isSelected
                            ? 'text-primary border-primary bg-petal'
                            : 'text-gray-700 hover:text-gray-900 bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                          }
                        `}
                        onClick={() => handleCategoryClick(category)}
                      >
                        <span>{category.name}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>

        {isCategoryDialogOpen && (
          <div
            className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-4 lg:hidden"
            onClick={() => setIsCategoryDialogOpen(false)}
          >
            <div
              className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[80vh] overflow-hidden"
              role="dialog"
              aria-modal="true"
              aria-labelledby="category-dialog-title"
              onClick={(event) => event.stopPropagation()}
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 id="category-dialog-title" className="text-lg font-semibold text-gray-900">
                  {t('categories')}
                </h2>
                <button
                  type="button"
                  onClick={() => setIsCategoryDialogOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                  aria-label="Close categories dialog"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              <div className="p-4 overflow-y-auto max-h-[calc(80vh-73px)]">
                <div className="space-y-2">
                  {displayCategories.map((category) => {
                    const isSelected = selectedCategory === category.id;
                    return (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => handleDialogCategoryClick(category)}
                        className={`
                          w-full flex items-center justify-between py-3 px-4
                          border rounded-lg transition-all text-sm font-medium
                          ${isSelected
                            ? 'text-primary border-primary bg-petal'
                            : 'text-gray-700 hover:text-gray-900 bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                          }
                        `}
                      >
                        <span>{category.name}</span>
                        {isSelected && (
                          <span className="w-2 h-2 rounded-full bg-primary" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        <style>{`
          .scrollbar-hide::-webkit-scrollbar { display: none; }
          .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        `}</style>
      </nav>  
    </div>
  );
};

export default Categories;
